import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/hooks/use-auth";
import { adminInspectSocialPreview } from "@/lib/admin-social-preview.functions";
import { toast } from "sonner";
import { RefreshCw, ExternalLink, ImageIcon, CheckCircle2, AlertTriangle, Download, Printer } from "lucide-react";
import { buildSocialPreviewCsv, openSocialPreviewPrintable } from "@/lib/social-preview-export";

export const Route = createFileRoute("/_authenticated/admin/social-preview")({
  head: () => ({ meta: [{ title: "Admin — Social preview" }, { name: "robots", content: "noindex" }] }),
  component: SocialPreviewPage,
});

type Result = any;

function SocialPreviewPage() {
  const { isAdmin, loading } = useAuth();
  const inspect = useServerFn(adminInspectSocialPreview);
  const [url, setUrl] = useState("https://elvion-group.nl/");
  const [result, setResult] = useState<Result | null>(null);

  const mutation = useMutation({
    mutationFn: (refresh: boolean) => inspect({ data: { url, refresh } }),
    onSuccess: (r) => {
      setResult(r);
      toast.success(`Meta opgehaald (HTTP ${r.httpStatus})`);
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Mislukt"),
  });

  if (loading) return <div className="p-8">Laden…</div>;
  if (!isAdmin) return <div className="p-8">Geen toegang.</div>;

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      <div>
        <h1 className="text-3xl font-bold">Social preview</h1>
        <p className="text-muted-foreground">
          Inspecteer og:image / twitter:image en forceer Facebook om de preview te herladen.
        </p>
      </div>

      <div className="rounded-xl border bg-card p-5 space-y-3">
        <label className="text-sm font-medium">Pagina-URL of pad</label>
        <div className="flex gap-2 flex-wrap">
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="flex-1 min-w-[240px] rounded-md border bg-background px-3 py-2 text-sm"
            placeholder="https://elvion-group.nl/… of /blog/mijn-post"
          />
          <button
            type="button"
            onClick={() => mutation.mutate(false)}
            disabled={mutation.isPending}
            className="inline-flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
          >
            Alleen inspecteren
          </button>
          <button
            type="button"
            onClick={() => mutation.mutate(true)}
            disabled={mutation.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${mutation.isPending ? "animate-spin" : ""}`} />
            Herlaad social previews
          </button>
        </div>
      </div>

      {result && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              const csv = buildSocialPreviewCsv(result);
              const slug = (result.url as string).replace(/^https?:\/\//, "").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").slice(0, 60) || "social-preview";
              const stamp = new Date().toISOString().slice(0, 10);
              import("@/lib/social-preview-export").then(({ downloadCsv }) => downloadCsv(`social-preview-${slug}-${stamp}.csv`, csv));
            }}
            className="inline-flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm font-medium hover:bg-muted"
          >
            <Download className="h-4 w-4" /> Download CSV
          </button>
          <button
            type="button"
            onClick={() => {
              const opened = openSocialPreviewPrintable(result);
              if (!opened) toast.error("Sta pop-ups toe om het PDF-rapport te openen.");
            }}
            className="inline-flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm font-medium hover:bg-muted"
          >
            <Printer className="h-4 w-4" /> PDF-rapport
          </button>
        </div>
      )}

      {result && <ResultView r={result} />}
    </div>
  );
}

function ResultView({ r }: { r: Result }) {
  return (
    <div className="space-y-6">
      <div className="rounded-xl border bg-card p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="font-semibold">Gerapporteerde metadata</h2>
          <span className="text-xs text-muted-foreground">
            HTTP {r.httpStatus} · {new Date(r.fetchedAt).toLocaleString("nl-NL")}
          </span>
        </div>
        <dl className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-x-4 gap-y-2 text-sm">
          <Row label="URL" value={r.url} mono />
          <Row label="title" value={r.meta.title} />
          <Row label="description" value={r.meta.description} />
          <Row label="canonical" value={r.meta.canonical} mono />
          <Row label="og:title" value={r.meta.ogTitle} />
          <Row label="og:description" value={r.meta.ogDescription} />
          <Row label="og:url" value={r.meta.ogUrl} mono />
          <Row label="og:type" value={r.meta.ogType} />
          <Row label="twitter:card" value={r.meta.twitterCard} />
        </dl>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <ImageCard label="og:image" check={r.ogImageCheck} />
        <ImageCard label="twitter:image" check={r.twitterImageCheck} />
      </div>

      {r.facebook.requested && (
        <div className="rounded-xl border bg-card p-5">
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            {r.facebook.ok ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-amber-600" />
            )}
            Facebook scrape-refresh
          </h2>
          {r.facebook.ok ? (
            <div className="text-sm space-y-1">
              <div>Status: <span className="font-mono">{r.facebook.status}</span></div>
              {r.facebook.title && <div>Titel: {r.facebook.title}</div>}
              {r.facebook.image && (
                <div className="break-all">
                  Preview-image: <a className="text-primary underline" href={r.facebook.image} target="_blank" rel="noreferrer">{r.facebook.image}</a>
                </div>
              )}
              {r.facebook.updatedTime && (
                <div className="text-xs text-muted-foreground">Bijgewerkt: {r.facebook.updatedTime}</div>
              )}
            </div>
          ) : (
            <div className="text-sm text-amber-800">
              {r.facebook.error ?? "Refresh niet gelukt."} Gebruik de handmatige Debugger-link hieronder.
            </div>
          )}
        </div>
      )}

      <JsonLdCard blocks={r.jsonLd ?? []} />

      <div className="rounded-xl border bg-card p-5">
        <h2 className="font-semibold mb-3">Handmatige debuggers</h2>
        <ul className="space-y-2 text-sm">
          <DebugLink label="Facebook Sharing Debugger (klik ‘Scrape Again’)" href={r.debuggerLinks.facebook} />
          <DebugLink label="LinkedIn Post Inspector" href={r.debuggerLinks.linkedin} />
          <DebugLink label="WhatsApp (gebruikt Facebook-cache)" href={r.debuggerLinks.whatsapp} />
        </ul>
      </div>
    </div>
  );
}

function JsonLdCard({ blocks }: { blocks: any[] }) {
  const totalErrors = blocks.reduce((s, b) => s + (b.errors?.length ?? 0), 0);
  const totalWarnings = blocks.reduce((s, b) => s + (b.warnings?.length ?? 0), 0);
  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-semibold flex items-center gap-2">
          {totalErrors === 0 ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          ) : (
            <AlertTriangle className="h-4 w-4 text-red-600" />
          )}
          JSON-LD (Schema.org) validatie
        </h2>
        <span className="text-xs text-muted-foreground">
          {blocks.length} blok(ken) · {totalErrors} fout(en) · {totalWarnings} waarschuwing(en)
        </span>
      </div>
      {blocks.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">Geen JSON-LD gevonden op deze pagina.</p>
      ) : (
        <div className="space-y-3">
          {blocks.map((b) => (
            <div key={b.index} className="rounded border bg-background p-3 text-sm">
              <div className="flex items-center justify-between mb-1">
                <div className="font-medium">Blok #{b.index + 1} — {b.types?.join(", ") || "geen @type"}</div>
                {b.errors?.length === 0 ? (
                  <span className="text-xs rounded bg-emerald-100 px-2 py-0.5 text-emerald-800">geldig</span>
                ) : (
                  <span className="text-xs rounded bg-red-100 px-2 py-0.5 text-red-800">{b.errors.length} fout(en)</span>
                )}
              </div>
              {b.errors?.length > 0 && (
                <ul className="text-xs text-red-800 list-disc pl-4 space-y-0.5">
                  {b.errors.map((e: string, i: number) => <li key={i}>{e}</li>)}
                </ul>
              )}
              {b.warnings?.length > 0 && (
                <ul className="text-xs text-amber-800 list-disc pl-4 mt-1 space-y-0.5">
                  {b.warnings.map((w: string, i: number) => <li key={i}>{w}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`${mono ? "font-mono text-xs break-all" : "break-words"}`}>
        {value ?? <span className="text-muted-foreground italic">ontbreekt</span>}
      </dd>
    </>
  );
}

function ImageCard({ label, check }: { label: string; check: any }) {
  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold flex items-center gap-2">
          <ImageIcon className="h-4 w-4" /> {label}
        </h3>
        {check && (
          check.ok ? (
            <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800">
              <CheckCircle2 className="h-3 w-3" /> {check.status}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded bg-red-100 px-2 py-0.5 text-xs text-red-800">
              <AlertTriangle className="h-3 w-3" /> {check.status || "fout"}
            </span>
          )
        )}
      </div>
      {!check ? (
        <p className="text-sm text-muted-foreground italic">Geen {label} gedefinieerd op deze pagina.</p>
      ) : (
        <div className="space-y-2 text-sm">
          <div className="break-all font-mono text-xs">
            <a href={check.url} target="_blank" rel="noreferrer" className="text-primary underline">{check.url}</a>
          </div>
          <div className="text-xs text-muted-foreground">
            {check.contentType ?? "?"} {check.contentLength ? `· ${Math.round(Number(check.contentLength) / 1024)} KB` : ""}
            {check.dimensions && ` · ${check.dimensions.width}×${check.dimensions.height}px (${check.dimensions.format})`}
          </div>
          {check.ok && /^image\//.test(check.contentType ?? "") && (
            <img src={check.url} alt={label} className="mt-2 max-h-48 rounded border" />
          )}
          {check.assessment && <PlatformAssessment a={check.assessment} />}
          {check.ok && !check.dimensions && (
            <p className="text-xs text-amber-700">Afmetingen konden niet worden bepaald (onbekend formaat).</p>
          )}
        </div>
      )}
    </div>
  );
}

function PlatformAssessment({ a }: { a: any }) {
  const rows: Array<{ name: string; d: any }> = [
    { name: "Facebook", d: a.facebook },
    { name: "LinkedIn", d: a.linkedin },
    { name: "Twitter/X", d: a.twitter },
  ];
  return (
    <div className="rounded border bg-muted/40 p-2 space-y-1">
      {rows.map(({ name, d }) => {
        const tone = d.recommended ? "emerald" : d.ok ? "amber" : "red";
        const label = d.recommended ? "voldoet aan aanbevelingen" : d.ok ? "voldoet minimaal" : "onder minimum";
        const dot =
          tone === "emerald" ? "bg-emerald-500" : tone === "amber" ? "bg-amber-500" : "bg-red-500";
        return (
          <div key={name} className="text-xs">
            <div className="flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full ${dot}`} />
              <span className="font-medium">{name}:</span>
              <span className="text-muted-foreground">{label}</span>
            </div>
            {d.notes.length > 0 && (
              <ul className="pl-5 text-muted-foreground list-disc">
                {d.notes.map((n: string, i: number) => <li key={i}>{n}</li>)}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DebugLink({ label, href }: { label: string; href: string }) {
  return (
    <li>
      <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-primary hover:underline">
        <ExternalLink className="h-3 w-3" />
        {label}
      </a>
    </li>
  );
}
